import { useMemo, useState, useCallback } from "react";
import { cn } from "@/lib/utils";
import { useTranslation } from "react-i18next";
import { useLanguage } from "@/i18n";
import {
    useStreakCalendar,
    useStreakMilestones,
    type StreakCalendarDay,
    type StreakMilestone,
    type UserStreakMilestone,
} from "@/hooks/use-gamification";
import { Skeleton } from "@/components/ui/skeleton";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Flame, Trophy, ChevronLeft, ChevronRight, Snowflake } from "lucide-react";

interface StreakCalendarProps {
    currentStreak: number;
    allMilestones: StreakMilestone[];
    className?: string;
}

const getActivityLevel = (day: StreakCalendarDay | null): number => {
    if (!day) return 0;
    if (day.freeze_used) return 1;
    if (day.practice_sessions === 0 && day.xp_earned === 0) return 0;
    if (day.xp_earned >= 200) return 3;
    if (day.xp_earned >= 50) return 2;
    return 1;
};

/**
 * Four activity levels, warmest last.
 *
 * The old ramp started at `orange-200` and was so close to the stone it sat on
 * that a learner with two active days in October could not find them at a
 * glance — which is the entire job of this grid. Each step is now far enough
 * from the last to be counted, and level 0 is a clearly *empty* stone rather
 * than a faint tint that reads as "a little bit".
 */
const levelColors: Record<number, string> = {
    0: "bg-stone-100 dark:bg-stone-800",
    1: "bg-amber-200 dark:bg-amber-900/50",
    2: "bg-orange-400 dark:bg-orange-700/70",
    3: "bg-gradient-to-br from-orange-500 to-red-500 dark:from-orange-500 dark:to-red-600",
};

const freezeColor = "bg-cyan-100 dark:bg-cyan-900/40";

/**
 * Month and weekday names come from `Intl` against the app's active language,
 * not from a hardcoded English list. A Vietnamese reader with an English OS
 * got "October 2026" here while the rest of the dialog was in Vietnamese, and
 * the single-letter weekday headers were English abbreviations under an
 * English-language product. `locale` comes from `useLanguage()`, which is the
 * one place that knows the reader's choice; a bare `toLocaleDateString()`
 * formats against the *browser's* locale instead.
 */
function monthNamesFor(locale: string): string[] {
    const out: string[] = [];
    for (let m = 0; m < 12; m++) {
        out.push(
            new Intl.DateTimeFormat(locale, { month: "long", timeZone: "UTC" }).format(
                new Date(Date.UTC(2024, m, 1)),
            ),
        );
    }
    return out;
}

function weekdayNamesFor(locale: string): string[] {
    // 2024-01-01 was a Monday, so these seven dates are Mon..Sun in order.
    return Array.from({ length: 7 }, (_, i) =>
        new Intl.DateTimeFormat(locale, { weekday: "short", timeZone: "UTC" }).format(
            new Date(Date.UTC(2024, 0, 1 + i)),
        ),
    );
}

function formatDate(date: Date): string {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, "0");
    const d = String(date.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
}

interface CalendarCell {
    dateStr: string;
    date: Date;
    isToday: boolean;
    isFuture: boolean;
    rpcDay: StreakCalendarDay | null;
    isMilestone: boolean;
    milestoneInfo: StreakMilestone | null;
}

function buildMonthGrid(
    year: number,
    month: number,
    today: Date,
    rpcMap: Map<string, StreakCalendarDay>,
    nextMilestone: StreakMilestone | null,
    currentStreak: number,
    achievedMilestoneMap: Map<string, UserStreakMilestone>
): CalendarCell[] {
    const todayStr = formatDate(today);
    const firstOfMonth = new Date(year, month, 1);
    const lastOfMonth = new Date(year, month + 1, 0);
    const startDow = (firstOfMonth.getDay() + 6) % 7;

    const cells: CalendarCell[] = [];

    for (let i = 0; i < startDow; i++) {
        cells.push({
            dateStr: "",
            date: new Date(0),
            isToday: false,
            isFuture: false,
            rpcDay: null,
            isMilestone: false,
            milestoneInfo: null,
        });
    }

    for (let day = 1; day <= lastOfMonth.getDate(); day++) {
        const d = new Date(year, month, day);
        const ds = formatDate(d);
        const isFuture = d > today && ds !== todayStr;
        let isMilestone = false;
        let milestoneInfo: StreakMilestone | null = null;

        if (isFuture && nextMilestone) {
            const daysDiff = Math.round(
                (d.getTime() - today.getTime()) / (1000 * 60 * 60 * 24)
            );
            if (daysDiff === nextMilestone.streak_days - currentStreak) {
                isMilestone = true;
                milestoneInfo = nextMilestone;
            }
        } else if (!isFuture) {
            const achieved = achievedMilestoneMap.get(ds);
            if (achieved?.streak_milestones) {
                isMilestone = true;
                milestoneInfo = achieved.streak_milestones;
            }
        }

        cells.push({
            dateStr: ds,
            date: d,
            isToday: ds === todayStr,
            isFuture,
            rpcDay: rpcMap.get(ds) ?? null,
            isMilestone,
            milestoneInfo,
        });
    }

    return cells;
}

export const StreakCalendar = ({
    currentStreak,
    allMilestones,
    className,
}: StreakCalendarProps) => {
    const { t } = useTranslation();
    const language = useLanguage();
    const locale = language?.locale ?? "en";

    const weekdays = useMemo(() => weekdayNamesFor(locale), [locale]);
    const monthNames = useMemo(() => monthNamesFor(locale), [locale]);

    const now = new Date();
    const [viewYear, setViewYear] = useState(now.getFullYear());
    const [viewMonth, setViewMonth] = useState(now.getMonth());

    const today = useMemo(() => {
        const t = new Date();
        t.setHours(0, 0, 0, 0);
        return t;
    }, []);

    const monthStart = useMemo(() => new Date(viewYear, viewMonth, 1), [viewYear, viewMonth]);

    const daysToFetch = useMemo(() => {
        if (monthStart >= today) return 35;
        const diff = Math.ceil(
            (today.getTime() - monthStart.getTime()) / (1000 * 60 * 60 * 24)
        ) + 1;
        return Math.max(35, diff);
    }, [monthStart, today]);

    const { data: calendarDays, isLoading } = useStreakCalendar(daysToFetch);
    const { userMilestones } = useStreakMilestones();

    const rpcMap = useMemo(() => {
        const map = new Map<string, StreakCalendarDay>();
        if (calendarDays) {
            for (const d of calendarDays) {
                map.set(d.activity_date, d);
            }
        }
        return map;
    }, [calendarDays]);

    const achievedMilestoneMap = useMemo(() => {
        const map = new Map<string, UserStreakMilestone>();
        if (userMilestones) {
            for (const um of userMilestones) {
                if (um.achieved_at) {
                    const dateKey = new Date(um.achieved_at).toISOString().split("T")[0];
                    map.set(dateKey, um);
                }
            }
        }
        return map;
    }, [userMilestones]);

    const nextMilestone = useMemo(() => {
        const sorted = [...allMilestones].sort((a, b) => a.streak_days - b.streak_days);
        return sorted.find((m) => m.streak_days > currentStreak) ?? null;
    }, [allMilestones, currentStreak]);

    const gridData = useMemo(
        () => buildMonthGrid(viewYear, viewMonth, today, rpcMap, nextMilestone, currentStreak, achievedMilestoneMap),
        [viewYear, viewMonth, today, rpcMap, nextMilestone, currentStreak, achievedMilestoneMap]
    );

    const activeDays =
        calendarDays?.filter((d) => d.practice_sessions > 0 || d.xp_earned > 0).length ?? 0;

    const monthLabel = `${monthNames[viewMonth]} ${viewYear}`;

    const isCurrentMonth = viewYear === today.getFullYear() && viewMonth === today.getMonth();

    const canGoPrev = useMemo(() => {
        const minDate = new Date(today);
        minDate.setMonth(minDate.getMonth() - 12);
        return viewYear > minDate.getFullYear() ||
            (viewYear === minDate.getFullYear() && viewMonth > minDate.getMonth());
    }, [viewYear, viewMonth, today]);

    const canGoNext = useMemo(() => {
        const maxDate = new Date(today);
        maxDate.setMonth(maxDate.getMonth() + 6);
        return viewYear < maxDate.getFullYear() ||
            (viewYear === maxDate.getFullYear() && viewMonth < maxDate.getMonth());
    }, [viewYear, viewMonth, today]);

    const goToPrevMonth = useCallback(() => {
        if (viewMonth === 0) {
            setViewMonth(11);
            setViewYear((y) => y - 1);
        } else {
            setViewMonth((m) => m - 1);
        }
    }, [viewMonth]);

    const goToNextMonth = useCallback(() => {
        if (viewMonth === 11) {
            setViewMonth(0);
            setViewYear((y) => y + 1);
        } else {
            setViewMonth((m) => m + 1);
        }
    }, [viewMonth]);

    const goToToday = useCallback(() => {
        const t = new Date();
        setViewYear(t.getFullYear());
        setViewMonth(t.getMonth());
    }, []);

    const milestoneTooltip = (m: StreakMilestone) => {
        const parts = [m.badge_name];
        const rewards: string[] = [];
        if (m.xp_bonus > 0) rewards.push(`+${m.xp_bonus} XP`);
        if (m.freezes_awarded > 0)
            rewards.push(`+${m.freezes_awarded} freeze${m.freezes_awarded > 1 ? "s" : ""}`);
        if (rewards.length > 0) parts.push(rewards.join(", "));
        return parts.join(" — ");
    };

    if (isLoading) {
        return (
            <div className={cn("space-y-3", className)} role="status" aria-busy="true">
                <span className="sr-only">{t("gamification.streak.loading", "Loading activity…")}</span>
                <div className="flex items-center justify-between">
                    <Skeleton className="h-7 w-32" />
                    <Skeleton className="h-4 w-16" />
                </div>
                <div className="grid grid-cols-7 justify-items-center gap-1.5">
                    {weekdays.map((day) => (
                        <Skeleton key={day} className="h-3 w-7" />
                    ))}
                </div>
                <div className="grid grid-cols-7 justify-items-center gap-1.5">
                    {Array.from({ length: 35 }).map((_, i) => (
                        <Skeleton key={i} className="h-7 w-7 rounded-lg" />
                    ))}
                </div>
            </div>
        );
    }

    const renderCell = (cell: CalendarCell, idx: number) => {
        if (!cell.dateStr) {
            return <div key={`empty-${idx}`} className="h-7 w-7" />;
        }

        const level = cell.isFuture ? 0 : getActivityLevel(cell.rpcDay);
        const isFreeze = !cell.isFuture && !!cell.rpcDay?.freeze_used;
        const dateNum = cell.date.getDate();
        const dateLabel = new Intl.DateTimeFormat(locale, {
            month: "short",
            day: "numeric",
            timeZone: "UTC",
        }).format(cell.date);

        const xpInfo = cell.rpcDay
            ? isFreeze
                ? "Streak protected by freeze"
                : `${cell.rpcDay.xp_earned} XP, ${cell.rpcDay.practice_sessions} sessions`
            : "No activity yet";

        const cellContent = (
            <div
                className={cn(
                    "relative flex h-7 w-7 items-center justify-center rounded-lg text-[11px] font-medium tabular-nums leading-none transition-colors",
                    cell.isFuture
                        ? "border border-dashed border-stone-200 text-stone-400 dark:border-stone-800 dark:text-stone-600"
                        : isFreeze
                          ? freezeColor
                          : levelColors[level],
                    cell.isToday && "ring-2 ring-primary ring-offset-1 ring-offset-background",
                    cell.isMilestone &&
                        "ring-2 ring-amber-400 ring-offset-1 ring-offset-background",
                    !cell.isFuture && level > 0 && !isFreeze
                        ? "text-orange-950 dark:text-orange-100"
                        : !cell.isFuture && isFreeze
                          ? "text-cyan-700 dark:text-cyan-300"
                          : !cell.isFuture && "text-stone-500 dark:text-stone-400"
                )}
            >
                {dateNum}
                {isFreeze && (
                    <Snowflake className="absolute -right-1 -top-1 h-2.5 w-2.5 text-cyan-500" />
                )}
                {cell.isMilestone && (
                    <div className="absolute -bottom-1 -right-1 flex h-3 w-3 items-center justify-center rounded-full bg-background shadow-sm">
                        <Trophy className="h-2 w-2 text-amber-500" />
                    </div>
                )}
            </div>
        );

        if (cell.isMilestone && cell.milestoneInfo) {
            return (
                <TooltipProvider key={cell.dateStr}>
                    <Tooltip>
                        <TooltipTrigger asChild>{cellContent}</TooltipTrigger>
                        <TooltipContent side="top" className="text-xs">
                            <div className="flex items-center gap-1.5 font-medium">
                                <Trophy className="w-3 h-3 text-amber-500" />
                                {milestoneTooltip(cell.milestoneInfo)}
                            </div>
                        </TooltipContent>
                    </Tooltip>
                </TooltipProvider>
            );
        }

        return (
            <div key={cell.dateStr} title={`${dateLabel}: ${xpInfo}`}>
                {cellContent}
            </div>
        );
    };

    return (
        <div className={cn("space-y-3", className)}>
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-0.5">
                    <button
                        onClick={goToPrevMonth}
                        disabled={!canGoPrev}
                        aria-label={t("gamification.streak.previousMonth", "Previous month")}
                        className={cn(
                            "flex h-7 w-7 items-center justify-center rounded-lg text-stone-500 transition-colors hover:bg-stone-100 hover:text-stone-900 dark:text-stone-400 dark:hover:bg-stone-800",
                            !canGoPrev && "cursor-not-allowed opacity-30 hover:bg-transparent"
                        )}
                    >
                        <ChevronLeft className="h-4 w-4" />
                    </button>
                    <span className="min-w-[7.5rem] text-center text-sm font-semibold text-stone-900 dark:text-stone-50">
                        {monthLabel}
                    </span>
                    <button
                        onClick={goToNextMonth}
                        disabled={!canGoNext}
                        aria-label={t("gamification.streak.nextMonth", "Next month")}
                        className={cn(
                            "flex h-7 w-7 items-center justify-center rounded-lg text-stone-500 transition-colors hover:bg-stone-100 hover:text-stone-900 dark:text-stone-400 dark:hover:bg-stone-800",
                            !canGoNext && "cursor-not-allowed opacity-30 hover:bg-transparent"
                        )}
                    >
                        <ChevronRight className="h-4 w-4" />
                    </button>
                    {!isCurrentMonth && (
                        <button
                            onClick={goToToday}
                            className="ml-1 rounded-full border border-stone-200 px-2 py-0.5 text-[11px] font-medium text-stone-600 transition-colors hover:border-primary/40 hover:text-primary dark:border-stone-800 dark:text-stone-400"
                        >
                            {t("common.actions.today", "Today")}
                        </button>
                    )}
                </div>
                <p className="inline-flex items-center gap-1.5 rounded-full bg-amber-50 px-2 py-1 text-xs font-semibold text-amber-800 dark:bg-amber-950/40 dark:text-amber-300">
                    <Flame className="h-3.5 w-3.5" aria-hidden="true" />
                    {t("gamification.streak.activeDays", { defaultValue: "{{count}} active", count: activeDays })}
                </p>
            </div>

            <div className="grid grid-cols-7 justify-items-center gap-1.5">
                {weekdays.map((day) => (
                    <div
                        key={day}
                        className="pb-1 text-center text-[11px] font-semibold uppercase tracking-wider text-stone-500 dark:text-stone-400"
                    >
                        {day}
                    </div>
                ))}
            </div>
            <div className="grid grid-cols-7 justify-items-center gap-1.5">
                {gridData.map((cell, i) => renderCell(cell, i))}
            </div>

            <div className="flex items-center justify-end gap-1.5 border-t border-stone-200/70 pt-2.5 text-[11px] text-stone-500 dark:border-stone-800 dark:text-stone-400">
                <span>{t("gamification.streak.legendLess", "Less")}</span>
                {([0, 1, 2, 3] as const).map((level) => (
                    <div
                        key={level}
                        className={cn("h-3 w-3 rounded-[4px]", levelColors[level])}
                    />
                ))}
                <span>{t("gamification.streak.legendMore", "More")}</span>
                <span className="mx-1" aria-hidden="true">|</span>
                <Snowflake className="h-3 w-3 text-cyan-500" aria-hidden="true" />
                <span>{t("gamification.streak.legendFreeze", "Freeze")}</span>
                {nextMilestone && (
                    <>
                        <span className="mx-1" aria-hidden="true">|</span>
                        <Trophy className="h-3 w-3 text-amber-500" aria-hidden="true" />
                        <span>{t("gamification.streak.legendMilestone", "Milestone")}</span>
                    </>
                )}
            </div>
        </div>
    );
};

export default StreakCalendar;
